import * as React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './auth-context';

interface ProtectedRouteProps {
    requiredRoles?: string[];
    redirectTo?: string;
    isPublic?: boolean;
    // Blocks a platform admin who has "entered" a tenant (see the User interface's
    // isPlatformAdminImpersonation doc comment in auth-context.tsx) — that session has no
    // real User row in this tenant, so pages that manage "my own" tenant User data (like
    // the profile page) don't apply to it even though the Administrator role check passes.
    blockPlatformAdminImpersonation?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredRoles, redirectTo = '/login', isPublic = false, blockPlatformAdminImpersonation = false }) => {
    const { isAuthenticated, userRoles, user, isLoading } = useAuth();

    // Wait until loading is complete
    if (isLoading) {
        return null; // Or render a loading spinner: <div>Loading...</div>
    }

    // Redirect authenticated users from public routes to dashboard
    if (isPublic && isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Redirect unauthenticated users from protected routes to login
    if (!isAuthenticated && !isPublic) {
        return <Navigate to={redirectTo} replace />;
    }

    // Check role-based access for protected routes
    if (requiredRoles && !requiredRoles.some(role => userRoles.includes(role))) {
        return <Navigate to="/unauthorized" replace />;
    }

    if (blockPlatformAdminImpersonation && user?.isPlatformAdminImpersonation) {
        return <Navigate to="/unauthorized" replace />;
    }

    return <Outlet />;
};